import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { incomingInvoicesAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/useIsMobile';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import { PackagePlus, History, Truck } from 'lucide-react';
import InvoiceReviewModal from '@/components/InvoiceReviewModal';

const STATUS_CONFIG = {
  pending:            { label: 'In Transit',         bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400' },
  pending_revised:    { label: 'In Transit (Updated)', bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400' },
  supplier_responded: { label: 'In Transit',         bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400' },
  disputed:           { label: 'In Transit',         bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400' },
  acknowledged:       { label: 'Received (GRN)',     bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400' },
  cancelled:          { label: 'Cancelled',          bg: 'bg-gray-200 dark:bg-gray-700',      text: 'text-gray-600 dark:text-gray-300' },
  rejected:           { label: 'Rejected',           bg: 'bg-red-100 dark:bg-red-900/30',     text: 'text-red-700 dark:text-red-400' },
};

// Row-level background for GRN status
function rowGrnClass(status) {
  if (status === 'acknowledged') return 'border-l-4 border-green-400 bg-green-50 dark:bg-green-900/10';
  if (['pending', 'pending_revised', 'supplier_responded'].includes(status)) return 'border-l-4 border-yellow-400 bg-yellow-50 dark:bg-yellow-900/10';
  if (status === 'cancelled') return 'border-l-4 border-gray-400 bg-gray-50 dark:bg-gray-800/50';
  return '';
}

function StatusBadge({ status, cancelReason }) {
  const cfg = STATUS_CONFIG[status] || { label: status, bg: 'bg-gray-100', text: 'text-gray-600' };
  const label = status === 'cancelled' && cancelReason === 'sale_deleted'
    ? 'Cancelled (Bill deleted by supplier)'
    : cfg.label;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      {label}
    </span>
  );
}

function formatDate(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
}

function formatCurrency(n) {
  return `₹${parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const ACTIONABLE_STATUSES = 'pending,pending_revised,supplier_responded,cancelled';

export default function IncomingInvoices() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [showAll, setShowAll] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [acceptingAll, setAcceptingAll] = useState(false);

  const statusFilter = showAll ? undefined : ACTIONABLE_STATUSES;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['incoming-invoices', statusFilter],
    queryFn: () => incomingInvoicesAPI.getAll(statusFilter ? { status: statusFilter } : {}),
    select: r => r.data,
    staleTime: 60_000,
  });

  const invoices = data?.invoices ?? [];
  const total = data?.total ?? 0;

  // Live-sync: when supplier edits an in-transit invoice, refetch immediately
  useEffect(() => {
    const storeId = user?.store_id;
    if (!storeId) return;
    const channel = supabase.channel(`invoices-${storeId}`)
      .on('broadcast', { event: 'invoice_changed' }, () => {
        refetch();
        queryClient.invalidateQueries({ queryKey: ['incoming-invoice-count'] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.store_id, refetch, queryClient]);

  const handleReviewDone = useCallback(() => {
    setSelectedInvoice(null);
    refetch();
    queryClient.invalidateQueries({ queryKey: ['incoming-invoice-count'] });
    queryClient.invalidateQueries({ queryKey: ['stock-products'] });
    queryClient.invalidateQueries({ queryKey: ['stock-reorder'] });
  }, [refetch, queryClient]);

  const handleAcceptAll = useCallback(async () => {
    const pending = invoices.filter(inv => ['pending', 'pending_revised', 'supplier_responded'].includes(inv.status));
    if (!pending.length) return;
    if (!window.confirm(`Accept all ${pending.length} pending invoice${pending.length > 1 ? 's' : ''}? This will add items to your stock.`)) return;
    setAcceptingAll(true);
    let ok = 0;
    for (const inv of pending) {
      try {
        const detail = await incomingInvoicesAPI.getOne(inv.id);
        const items = (detail.data?.items || []).map(it => ({ ...it, qty: parseFloat(it.qty || 0) }));
        await incomingInvoicesAPI.acknowledge(inv.id, { acknowledged_items: items, note: 'Bulk accept' });
        ok++;
      } catch { /* skip failed ones */ }
    }
    setAcceptingAll(false);
    toast.success(`${ok} invoice${ok !== 1 ? 's' : ''} accepted and added to stock.`);
    refetch();
    queryClient.invalidateQueries({ queryKey: ['incoming-invoice-count'] });
    queryClient.invalidateQueries({ queryKey: ['stock-products'] });
    queryClient.invalidateQueries({ queryKey: ['stock-reorder'] });
  }, [invoices, refetch, queryClient]);

  const openInvoice = useCallback(async (inv) => {
    try {
      const res = await incomingInvoicesAPI.getOne(inv.id);
      setSelectedInvoice(res.data);
    } catch {
      setSelectedInvoice(inv);
    }
  }, []);

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-gray-500 dark:text-gray-400 text-sm">Loading incoming invoices…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-red-500 text-sm">Failed to load incoming invoices.</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col min-h-0 bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700">
        {/* Nav toggle row */}
        <div className="flex items-center justify-between px-4 py-3">
          <h2 className="text-base font-semibold text-foreground">
            {isMobile ? 'Purchase' : 'Incoming Invoices'}
          </h2>
          {isMobile ? (
            <div className="inline-flex items-center gap-0.5 bg-muted rounded-lg p-0.5">
              <button
                onClick={() => navigate('/purchases')}
                className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all text-muted-foreground hover:text-foreground whitespace-nowrap"
              >
                <PackagePlus className="w-3 h-3 shrink-0" />
                Record
              </button>
              <button
                onClick={() => navigate('/purchases?view=history')}
                className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all text-muted-foreground hover:text-foreground whitespace-nowrap"
              >
                <History className="w-3 h-3 shrink-0" />
                History
              </button>
              <button className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all bg-background text-foreground shadow-sm whitespace-nowrap">
                <Truck className="w-3 h-3 shrink-0" />
                Incoming
              </button>
            </div>
          ) : (
            <div className="inline-flex items-center gap-1 bg-muted rounded-lg p-1">
              <button
                onClick={() => navigate('/purchases')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all text-muted-foreground hover:text-foreground whitespace-nowrap"
              >
                <PackagePlus className="w-3.5 h-3.5 shrink-0" />
                Record Purchase
              </button>
              <button
                onClick={() => navigate('/purchases?view=history')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all text-muted-foreground hover:text-foreground whitespace-nowrap"
              >
                <History className="w-3.5 h-3.5 shrink-0" />
                Purchase History
              </button>
              <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all bg-background text-foreground shadow-sm whitespace-nowrap">
                <Truck className="w-3.5 h-3.5 shrink-0" />
                Incoming Invoices
              </button>
            </div>
          )}
        </div>

        {/* Sub-header row — count + Accept All + Pending/All toggle */}
        <div className="flex items-center justify-between px-4 pb-3 gap-2">
          <p className="text-xs text-muted-foreground">
            {showAll ? `${total} total` : `${total} pending`}
          </p>
          <div className="flex items-center gap-2">
            {!showAll && invoices.filter(i => ['pending','pending_revised','supplier_responded'].includes(i.status)).length > 1 && (
              <button
                onClick={handleAcceptAll}
                disabled={acceptingAll}
                className="text-xs px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-medium transition-colors disabled:opacity-60"
              >
                {acceptingAll ? 'Accepting…' : 'Accept All'}
              </button>
            )}
            <div className="inline-flex items-center gap-0.5 bg-muted rounded-lg p-0.5">
              <button
                onClick={() => setShowAll(false)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all whitespace-nowrap ${
                  !showAll ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Pending
              </button>
              <button
                onClick={() => setShowAll(true)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all whitespace-nowrap ${
                  showAll ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                All
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice list */}
      <div className="flex-1 overflow-y-auto">
        {invoices.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full py-20 text-center px-6">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-gray-600 dark:text-gray-400 font-medium">No pending invoices from suppliers</p>
            <p className="text-gray-400 dark:text-gray-600 text-sm mt-1">
              {showAll ? 'No invoices found.' : 'Switch to "Show All" to see past invoices.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {invoices.map(inv => (
              <button
                key={inv.id}
                onClick={() => openInvoice(inv)}
                className={`w-full text-left px-4 py-3.5 hover:brightness-95 transition-colors ${rowGrnClass(inv.status)}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                        {inv.portal_name || 'Supplier'}
                      </span>
                      {inv.pos_bill_number && (
                        <span className="text-xs text-gray-400 dark:text-gray-500">#{inv.pos_bill_number}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                      <StatusBadge status={inv.status} cancelReason={inv.cancel_reason} />
                    </div>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      {formatDate(inv.invoice_date)} · {inv.payment_mode}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(inv.total_amount)}</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{formatDate(inv.created_at)}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Review modal */}
      {selectedInvoice && (
        <InvoiceReviewModal
          invoice={selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
          onDone={handleReviewDone}
        />
      )}
    </div>
  );
}
